import type { ComponentType } from 'react';
import type { MarketingHubSlug } from '@/lib/routing/navTree';
import { IllustrationSvg, type IllustrationSvgProps } from './IllustrationSvg';

type HubIllustrationProps = Omit<IllustrationSvgProps, 'idPrefix' | 'children'>;

function ServicesHubIllustration(props: HubIllustrationProps) {
  return (
    <IllustrationSvg idPrefix="hub-services" {...props}>
      <rect x="72" y="88" width="120" height="88" rx="12" stroke="url(#hub-services-grad)" strokeWidth="2.5" fill="var(--semantic-bg-secondary)" fillOpacity="0.65" />
      <rect x="108" y="64" width="120" height="88" rx="12" stroke="var(--color-primary)" strokeWidth="2" fill="var(--semantic-bg-secondary)" fillOpacity="0.8" />
      <rect x="144" y="112" width="120" height="88" rx="12" stroke="var(--color-secondary)" strokeWidth="2" fill="var(--semantic-bg-secondary)" fillOpacity="0.75" />
      <path d="M88 220h224" stroke="var(--color-primary)" strokeWidth="2" strokeLinecap="round" strokeOpacity="0.35" />
      <circle cx="200" cy="220" r="28" fill="url(#hub-services-grad)" fillOpacity="0.2" stroke="url(#hub-services-grad)" strokeWidth="2" />
      <path d="M200 208v24M188 220h24" stroke="url(#hub-services-grad)" strokeWidth="2.5" strokeLinecap="round" />
    </IllustrationSvg>
  );
}

function TechnologiesHubIllustration(props: HubIllustrationProps) {
  return (
    <IllustrationSvg idPrefix="hub-technologies" {...props}>
      <path
        d="M120 92h160l20 48H100l20-48z"
        stroke="url(#hub-technologies-grad)"
        strokeWidth="2.5"
        fill="var(--semantic-bg-secondary)"
        fillOpacity="0.7"
      />
      <rect x="128" y="148" width="144" height="72" rx="10" stroke="var(--color-primary)" strokeWidth="2" fill="var(--semantic-bg-secondary)" fillOpacity="0.65" />
      <path d="M148 172h24M148 188h56" stroke="var(--color-secondary)" strokeWidth="2" strokeLinecap="round" />
      <circle cx="272" cy="108" r="20" fill="url(#hub-technologies-grad)" fillOpacity="0.25" />
      <path d="M88 200c32-40 96-40 128 0" stroke="var(--color-primary)" strokeWidth="2" strokeLinecap="round" strokeOpacity="0.4" />
      <circle cx="96" cy="200" r="6" fill="var(--color-secondary)" />
      <circle cx="200" cy="176" r="6" fill="var(--color-primary)" />
      <circle cx="304" cy="200" r="6" fill="var(--color-secondary)" />
    </IllustrationSvg>
  );
}

function AiAgentsHubIllustration(props: HubIllustrationProps) {
  return (
    <IllustrationSvg idPrefix="hub-ai-agents" {...props}>
      <circle cx="200" cy="130" r="36" stroke="url(#hub-ai-agents-grad)" strokeWidth="2.5" fill="var(--semantic-bg-secondary)" fillOpacity="0.75" />
      <circle cx="120" cy="180" r="22" stroke="var(--color-primary)" strokeWidth="2" fill="var(--semantic-bg-secondary)" fillOpacity="0.8" />
      <circle cx="280" cy="180" r="22" stroke="var(--color-secondary)" strokeWidth="2" fill="var(--semantic-bg-secondary)" fillOpacity="0.8" />
      <circle cx="160" cy="88" r="14" stroke="var(--color-secondary)" strokeWidth="1.8" fill="var(--semantic-bg-secondary)" fillOpacity="0.85" />
      <circle cx="248" cy="88" r="14" stroke="var(--color-primary)" strokeWidth="1.8" fill="var(--semantic-bg-secondary)" fillOpacity="0.85" />
      <path
        d="M152 118l24 12 48-36M120 158l36 8M280 158l-36 8"
        stroke="url(#hub-ai-agents-grad)"
        strokeWidth="2"
        strokeLinecap="round"
        strokeOpacity="0.55"
      />
      <path d="M176 220h48" stroke="var(--color-primary)" strokeWidth="2" strokeLinecap="round" strokeOpacity="0.35" />
      <path d="M188 208l12 12 24-28" stroke="url(#hub-ai-agents-grad)" strokeWidth="2.5" strokeLinecap="round" strokeLinejoin="round" />
    </IllustrationSvg>
  );
}

function IndustriesHubIllustration(props: HubIllustrationProps) {
  return (
    <IllustrationSvg idPrefix="hub-industries" {...props}>
      <path
        d="M108 200V120h56v80M180 200V96h56v104M252 200v-64h56v64"
        stroke="url(#hub-industries-grad)"
        strokeWidth="2.5"
        strokeLinecap="round"
        fill="var(--semantic-bg-secondary)"
        fillOpacity="0.55"
      />
      <path d="M96 200h208" stroke="var(--color-primary)" strokeWidth="2.5" strokeLinecap="round" />
      <path d="M128 152h24M200 128h24M272 168h24" stroke="var(--color-secondary)" strokeWidth="2" strokeLinecap="round" strokeOpacity="0.7" />
      <circle cx="320" cy="108" r="28" stroke="var(--color-primary)" strokeWidth="2" fill="url(#hub-industries-grad)" fillOpacity="0.15" />
      <path d="M308 108h24M320 96v24" stroke="url(#hub-industries-grad)" strokeWidth="2" strokeLinecap="round" />
    </IllustrationSvg>
  );
}

function HireHubIllustration(props: HubIllustrationProps) {
  return (
    <IllustrationSvg idPrefix="hub-hire" {...props}>
      <rect x="100" y="108" width="200" height="112" rx="16" stroke="url(#hub-hire-grad)" strokeWidth="2.5" fill="var(--semantic-bg-secondary)" fillOpacity="0.7" />
      <circle cx="160" cy="168" r="22" fill="var(--color-primary)" fillOpacity="0.18" stroke="var(--color-primary)" strokeWidth="2" />
      <circle cx="240" cy="168" r="22" fill="var(--color-secondary)" fillOpacity="0.18" stroke="var(--color-secondary)" strokeWidth="2" />
      <path d="M148 156c6-12 28-12 34 0M218 156c6-12 28-12 34 0" stroke="var(--color-primary)" strokeWidth="2" strokeLinecap="round" />
      <rect x="148" y="76" width="104" height="48" rx="10" stroke="var(--color-secondary)" strokeWidth="2" fill="var(--semantic-bg-secondary)" fillOpacity="0.8" />
      <path d="M164 100h72" stroke="url(#hub-hire-grad)" strokeWidth="2" strokeLinecap="round" />
    </IllustrationSvg>
  );
}

function PortfolioHubIllustration(props: HubIllustrationProps) {
  return (
    <IllustrationSvg idPrefix="hub-portfolio" {...props}>
      <rect x="88" y="96" width="104" height="128" rx="14" stroke="var(--color-primary)" strokeWidth="2" fill="var(--semantic-bg-secondary)" fillOpacity="0.75" />
      <rect x="208" y="112" width="104" height="112" rx="14" stroke="var(--color-secondary)" strokeWidth="2" fill="var(--semantic-bg-secondary)" fillOpacity="0.75" />
      <path d="M112 132h56M112 152h40M232 136h56M232 156h32" stroke="url(#hub-portfolio-grad)" strokeWidth="2" strokeLinecap="round" strokeOpacity="0.65" />
      <circle cx="200" cy="220" r="24" fill="url(#hub-portfolio-grad)" fillOpacity="0.22" stroke="url(#hub-portfolio-grad)" strokeWidth="2" />
      <path d="M188 220l8 8 16-20" stroke="var(--semantic-bg-secondary)" strokeWidth="2.5" strokeLinecap="round" strokeLinejoin="round" />
    </IllustrationSvg>
  );
}

const HUB_ILLUSTRATIONS: Record<MarketingHubSlug, ComponentType<HubIllustrationProps>> = {
  services: ServicesHubIllustration,
  technologies: TechnologiesHubIllustration,
  'ai-agents': AiAgentsHubIllustration,
  industries: IndustriesHubIllustration,
  hire: HireHubIllustration,
  portfolio: PortfolioHubIllustration,
};

export function HubHeroIllustration({
  hub,
  ...props
}: HubIllustrationProps & { hub: MarketingHubSlug }) {
  const Component = HUB_ILLUSTRATIONS[hub] ?? ServicesHubIllustration;
  return <Component {...props} />;
}
